import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Plus, Trash2, Save, Edit2, X, AlertTriangle, Calendar, FileSpreadsheet, Info, Link2, CheckCircle2, AlertCircle, Radio, Key, RefreshCw, Trophy } from "lucide-react";
import api from "../../lib/api";
import { TOURNAMENT } from "../../lib/constants";
import { categoryMatches } from "../../lib/categories";
import { TeamLogo } from "../../components/UIBits";

const EMPTY_GAME = {
    game_num: "", category: TOURNAMENT.categories[0], day: "2026-07-04",
    time: "", time_end: "", ice: TOURNAMENT.ices[0],
    home_team_id: "", away_team_id: "",
    home_placeholder: "", away_placeholder: "",
    home_source: "", away_source: "",
    phase: "preliminaire",
};

const PHASE_LABEL = {
    preliminaire: "Préliminaire",
    quart: "Quart-finale",
    demi: "Demi-finale",
    finale: "Finale",
    finale_d2: "Finale D2",
    finale_d3: "Finale D3",
    elim: "Éliminatoire",
};

function GlobalMarkerPwd() {
    const [pwd, setPwd] = useState(null);
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        api.get("/admin/marker_password").then(({ data }) => setPwd(data.marker_password)).catch(() => {});
    }, []);

    const regen = async () => {
        if (!window.confirm("Régénérer le mot de passe LIVE global ?\n\nToutes les parties utiliseront le nouveau code — les marqueurs actuellement connectés resteront connectés jusqu'à expiration de leur session.")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/marker_password/regenerate");
            setPwd(data.marker_password);
        } finally { setBusy(false); }
    };

    const copy = async () => {
        if (!pwd) return;
        try {
            await navigator.clipboard.writeText(pwd);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* clipboard denied */ }
    };

    return (
        <div className="glass-card p-4 mb-4 border-l-4 border-[#7FD93C] flex flex-wrap items-center gap-3" data-testid="global-marker-pwd">
            <Key size={18} className="text-[#7FD93C] shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="label-mini text-[#7FD93C]">Mot de passe LIVE (marqueurs)</div>
                <div className="text-xs text-white/60">Utilisé pour toutes les parties · à partager avec vos marqueurs.</div>
            </div>
            <button
                onClick={copy}
                disabled={!pwd}
                className={`px-4 py-2 font-mono text-lg tracking-widest border transition ${copied ? "bg-[#7FD93C]/20 border-[#7FD93C] text-[#7FD93C]" : "bg-black/40 border-[#4FE0D5]/40 text-[#4FE0D5] hover:bg-[#4FE0D5]/10"}`}
                title="Cliquer pour copier"
                data-testid="global-marker-pwd-value"
            >
                {pwd || "······"}
            </button>
            <button
                onClick={regen}
                disabled={busy}
                className="btn-ghost flex items-center gap-1 text-xs"
                data-testid="btn-regen-global-pwd"
                title="Régénérer"
            >
                <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Régénérer
            </button>
            {copied && <span className="text-xs text-[#7FD93C]" data-testid="global-pwd-copied">Copié !</span>}
        </div>
    );
}

export default function AdminSchedule() {
    const [games, setGames] = useState([]);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // game_num | "new" | null
    const [form, setForm] = useState(EMPTY_GAME);
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [catFilter, setCatFilter] = useState("all");
    const [preview, setPreview] = useState(null); // { rows, unique_teams, stats, file, replace_all }
    const [overrides, setOverrides] = useState({}); // { key: team_id | "__create__" }
    const [importing, setImporting] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        const [g, t] = await Promise.all([api.get("/games"), api.get("/teams")]);
        setGames(g.data.sort((a, b) => (a.game_num || 0) - (b.game_num || 0)));
        setTeams(t.data);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const teamMap = Object.fromEntries(teams.map(t => [t.id, t]));
    const teamsByCat = (cat) => teams.filter(t => !cat || categoryMatches(cat, t.category)).sort((a, b) => a.name.localeCompare(b.name));

    const filtered = games.filter(g => catFilter === "all" || g.category === catFilter);

    const startNew = () => { setForm(EMPTY_GAME); setEditing("new"); };
    const startEdit = (g) => {
        setForm({
            game_num: g.game_num, category: g.category || TOURNAMENT.categories[0], day: g.day || "2026-07-04",
            time: g.time || "", time_end: g.time_end || "", ice: g.ice || TOURNAMENT.ices[0],
            home_team_id: g.home_team_id || "", away_team_id: g.away_team_id || "",
            home_placeholder: g.home_placeholder || "", away_placeholder: g.away_placeholder || "",
            home_source: g.home_source || "", away_source: g.away_source || "",
            phase: g.phase || "preliminaire",
        });
        setEditing(g.game_num);
    };
    const cancel = () => { setEditing(null); setForm(EMPTY_GAME); };

    const save = async (e) => {
        e?.preventDefault();
        const payload = { ...form, game_num: parseInt(form.game_num, 10) };
        try {
            if (editing === "new") {
                await api.post("/games", payload);
            } else {
                await api.put(`/games/${editing}`, payload);
            }
            await load();
            cancel();
        } catch (ex) {
            alert("Erreur : " + (ex?.response?.data?.detail || ex.message));
        }
    };

    const del = async (game_num) => {
        if (!window.confirm(`Supprimer la partie #${game_num} ?`)) return;
        await api.delete(`/games/${game_num}`);
        load();
    };

    const wipeAll = async () => {
        if (!window.confirm("⚠️ ATTENTION — Supprimer TOUTES les parties ? Cette action est irréversible.")) return;
        if (!window.confirm("Confirmation finale : supprimer TOUTES les parties ?")) return;
        await api.delete("/games");
        load();
    };

    const onUpload = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("default_day", "2026-07-04");
        setUploading(true); setUploadResult(null);
        try {
            const { data } = await api.post("/games/preview_xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
            // Pre-populate overrides with the auto-matched team_ids
            const initial = {};
            (data.unique_teams || []).forEach(u => {
                if (u.matched_team_id) initial[u.key] = u.matched_team_id;
                else initial[u.key] = "__create__";
            });
            setOverrides(initial);
            // replace_all defaults to FALSE; admin toggles in the modal
            setPreview({ ...data, replace_all: false });
        } catch (ex) {
            setUploadResult({ error: ex?.response?.data?.detail || ex.message });
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const cancelPreview = () => { setPreview(null); setOverrides({}); };

    const confirmImport = async () => {
        if (!preview) return;
        setImporting(true);
        try {
            const payload = {
                rows: preview.parsed_rows,
                default_day: "2026-07-04",
                replace_all: !!preview.replace_all,
                auto_create_teams: true,
                team_overrides: overrides,
            };
            const { data } = await api.post("/games/import", payload);
            setUploadResult({
                imported: data.imported,
                skipped_parse: preview.skipped_parse || [],
                skipped_rows: data.skipped || [],
            });
            await load();
            setPreview(null);
            setOverrides({});
        } catch (ex) {
            setUploadResult({ error: ex?.response?.data?.detail || ex.message });
        } finally {
            setImporting(false);
        }
    };

    const relinkTeams = async () => {
        if (!window.confirm("Relier les parties aux équipes inscrites ?\n\nCela va réassocier les parties dont le nom d'équipe correspond à une équipe existante (insensible à la casse/accents), puis supprimer les doublons créés automatiquement par un import précédent.")) return;
        try {
            const { data } = await api.post("/games/relink_teams");
            alert(`✓ Terminé.\n\n• Parties reliées : ${data.games_relinked}\n• Équipes en doublon supprimées : ${data.auto_teams_deleted}`);
            await load();
        } catch (ex) {
            alert("Erreur : " + (ex?.response?.data?.detail || ex.message));
        }
    };

    const advancePlayoffs = async () => {
        try {
            const { data } = await api.post("/admin/games/advance_playoffs");
            const total = data.total_updated ?? 0;
            const details = (data.per_category || []).filter(r => r.games_updated > 0)
                .map(r => `• ${r.category} : ${r.games_updated} partie(s)`).join("\n");
            alert(`✓ Avancement des éliminatoires terminé.\n\n${total} partie(s) mise(s) à jour.${details ? "\n\n" + details : ""}`);
            await load();
        } catch (ex) {
            alert("Erreur : " + (ex?.response?.data?.detail || ex.message));
        }
    };

    if (loading) return <div className="text-white/60">Chargement...</div>;

    const teamName = (g, side) => {
        const tid = side === "home" ? g.home_team_id : g.away_team_id;
        const t = teamMap[tid];
        if (t) return t.name;
        return side === "home" ? (g.home_placeholder || "—") : (g.away_placeholder || "—");
    };

    return (
        <div data-testid="admin-schedule">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
                <div>
                    <h1 className="font-display text-4xl uppercase tracking-widest">Horaire</h1>
                    <p className="text-white/60 text-sm mt-1">Importez votre horaire Excel ou créez les parties une par une.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={e => onUpload(e.target.files?.[0])} data-testid="schedule-file-input" />
                    <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-primary flex items-center gap-2" data-testid="btn-import-xlsx">
                        <Upload size={14} /> {uploading ? "Import..." : "Importer Excel"}
                    </button>
                    <button onClick={relinkTeams} className="btn-ghost flex items-center gap-2 border-[#4FE0D5]/30 text-[#4FE0D5] hover:border-[#4FE0D5]" data-testid="btn-relink-teams" title="Réassocier les parties aux équipes inscrites et supprimer les doublons auto-créés">
                        <Link2 size={14} /> Relier équipes
                    </button>
                    <button onClick={advancePlayoffs} className="btn-ghost flex items-center gap-2 border-[#7FD93C]/30 text-[#7FD93C] hover:border-[#7FD93C]" data-testid="btn-advance-playoffs" title="Résout seed:N / winner:X / loser:X → team_id automatiquement (aussi déclenché automatiquement à chaque finalisation)">
                        <Trophy size={14} /> Avancer éliminatoires
                    </button>
                    <button onClick={startNew} className="btn-ghost flex items-center gap-2 border-[#7FD93C]/30 text-[#7FD93C] hover:border-[#7FD93C]" data-testid="btn-new-game">
                        <Plus size={14} /> Nouvelle partie
                    </button>
                    <button onClick={wipeAll} className="btn-ghost text-rose-400 border-rose-400/30 hover:border-rose-400 flex items-center gap-2" data-testid="btn-wipe-games">
                        <Trash2 size={14} /> Tout effacer
                    </button>
                </div>
            </div>

            <GlobalMarkerPwd />

            <div className="glass-card p-4 mb-4 border-l-4 border-[#4FE0D5] text-sm">
                <div className="flex items-start gap-3">
                    <FileSpreadsheet size={18} className="text-[#4FE0D5] flex-shrink-0 mt-0.5" />
                    <div className="text-white/70">
                        <strong className="text-white">Format Excel attendu :</strong> colonnes <em>Numéro de partie, Emplacement, Heure début, Heure de fin, Catégorie, Équipe Locale, Équipe Receveur, Nom de la partie, Équipe locale temporaire, Équipe visiteur temporaire, Match de série</em>. Les équipes manquantes seront créées automatiquement.
                    </div>
                </div>
            </div>

            {uploadResult && (
                <div className={`glass-card p-4 mb-4 border-l-4 ${uploadResult.error ? "border-rose-500" : "border-[#7FD93C]"}`} data-testid="upload-result">
                    {uploadResult.error ? (
                        <div className="text-rose-400 flex items-center gap-2"><AlertTriangle size={16} /> Erreur : {uploadResult.error}</div>
                    ) : (
                        <div className="text-sm">
                            <div className="text-[#7FD93C] font-semibold">✓ {uploadResult.imported} parties importées</div>
                            {uploadResult.skipped_parse?.length > 0 && <div className="text-yellow-300 mt-1">⚠️ {uploadResult.skipped_parse.length} lignes ignorées</div>}
                        </div>
                    )}
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="label-mini">Filtre catégorie :</span>
                {["all", ...TOURNAMENT.categories].map(c => (
                    <button key={c} onClick={() => setCatFilter(c)} className={`px-3 py-1 text-xs font-display tracking-wider uppercase ${catFilter === c ? "bg-[#4FE0D5] text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`} data-testid={`sched-cat-${c}`}>
                        {c === "all" ? "Toutes" : c}
                    </button>
                ))}
                <span className="ml-auto text-xs text-white/50">{filtered.length} partie{filtered.length > 1 ? "s" : ""}</span>
            </div>

            {(editing !== null) && (
                <form onSubmit={save} className="glass-card p-5 mb-6 border-l-4 border-[#7FD93C]" data-testid="game-form">
                    <h3 className="font-display text-xl uppercase tracking-widest mb-4">{editing === "new" ? "Nouvelle partie" : `Modifier partie #${editing}`}</h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        <div><label className="label-mini">N°</label><input type="number" required disabled={editing !== "new"} className="input-field" value={form.game_num} onChange={e => setForm({ ...form, game_num: e.target.value })} data-testid="inp-game-num" /></div>
                        <div><label className="label-mini">Catégorie</label><select className="input-field" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="inp-game-cat">{TOURNAMENT.categories.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                        <div><label className="label-mini">Phase</label><select className="input-field" value={form.phase} onChange={e => setForm({ ...form, phase: e.target.value })} data-testid="inp-game-phase">{Object.entries(PHASE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
                        <div><label className="label-mini">Date</label><input type="date" required className="input-field" value={form.day} onChange={e => setForm({ ...form, day: e.target.value })} /></div>
                        <div><label className="label-mini">Heure début</label><input type="time" required className="input-field" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} /></div>
                        <div><label className="label-mini">Heure fin</label><input type="time" className="input-field" value={form.time_end} onChange={e => setForm({ ...form, time_end: e.target.value })} /></div>
                        <div className="lg:col-span-3"><label className="label-mini">Surface</label><select className="input-field" value={form.ice} onChange={e => setForm({ ...form, ice: e.target.value })}>{TOURNAMENT.ices.map(i => <option key={i} value={i}>{i}</option>)}</select></div>
                        <div>
                            <label className="label-mini">Équipe locale</label>
                            <select className="input-field" value={form.home_team_id} onChange={e => setForm({ ...form, home_team_id: e.target.value })} data-testid="inp-home-team">
                                <option value="">— Aucune (auto/placeholder) —</option>
                                {teamsByCat(form.category).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                            {!form.home_team_id && <input className="input-field mt-2" placeholder="Placeholder ex. Gagnant #15" value={form.home_placeholder} onChange={e => setForm({ ...form, home_placeholder: e.target.value })} />}
                            {form.phase !== "preliminaire" && !form.home_team_id && (
                                <input className="input-field mt-2 font-mono text-xs" placeholder="Source auto : seed:1 · winner:501 · loser:501" value={form.home_source} onChange={e => setForm({ ...form, home_source: e.target.value })} data-testid="inp-home-source" />
                            )}
                        </div>
                        <div>
                            <label className="label-mini">Équipe visiteur</label>
                            <select className="input-field" value={form.away_team_id} onChange={e => setForm({ ...form, away_team_id: e.target.value })} data-testid="inp-away-team">
                                <option value="">— Aucune (auto/placeholder) —</option>
                                {teamsByCat(form.category).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                            {!form.away_team_id && <input className="input-field mt-2" placeholder="Placeholder ex. Perdant #15" value={form.away_placeholder} onChange={e => setForm({ ...form, away_placeholder: e.target.value })} />}
                            {form.phase !== "preliminaire" && !form.away_team_id && (
                                <input className="input-field mt-2 font-mono text-xs" placeholder="Source auto : seed:1 · winner:501 · loser:501" value={form.away_source} onChange={e => setForm({ ...form, away_source: e.target.value })} data-testid="inp-away-source" />
                            )}
                        </div>
                    </div>
                    {form.phase !== "preliminaire" && (
                        <div className="text-[11px] text-white/50 mt-3 border-l-2 border-[#4FE0D5]/40 pl-3">
                            <div className="text-[#4FE0D5] uppercase tracking-widest text-[10px] font-bold mb-1">Auto-avancement</div>
                            Formats de source acceptés :
                            <code className="bg-black/40 px-1 mx-1">seed:1</code> (1er du classement),
                            <code className="bg-black/40 px-1 mx-1">winner:501</code> (vainqueur de la partie 501),
                            <code className="bg-black/40 px-1 mx-1">loser:501</code> (perdant de la partie 501).
                            Une équipe sélectionnée manuellement écrase toujours la source.
                        </div>
                    )}
                    <div className="flex gap-2 mt-4">
                        <button type="submit" className="btn-primary flex items-center gap-2" data-testid="btn-save-game"><Save size={14} /> Enregistrer</button>
                        <button type="button" onClick={cancel} className="btn-ghost"><X size={14} /> Annuler</button>
                    </div>
                </form>
            )}

            <div className="hidden lg:block overflow-x-auto"><table className="tx-table">
                <thead><tr><th>#</th><th>Cat.</th><th>Phase</th><th>Date</th><th>Heure</th><th>Surface</th><th>Locale</th><th>Visiteur</th><th>Score</th><th>Statut</th><th></th></tr></thead>
                <tbody>{filtered.map(g => (
                    <tr key={g.game_num} data-testid={`game-row-${g.game_num}`}>
                        <td className="font-display text-xl text-[#7FD93C]">#{g.game_num}</td>
                        <td><span className="px-2 py-0.5 text-[10px] tracking-widest font-bold bg-white/5">{g.category}</span></td>
                        <td className="text-xs text-white/60">{PHASE_LABEL[g.phase] || g.phase}</td>
                        <td className="text-xs">{g.day}</td>
                        <td>{g.time}{g.time_end ? ` — ${g.time_end}` : ""}</td>
                        <td className="text-xs text-[#4FE0D5]">{g.ice}</td>
                        <td className="text-sm"><div className="flex items-center gap-2"><TeamLogo team={teamMap[g.home_team_id]} size={24} /><span className="truncate">{teamName(g, "home")}</span></div></td>
                        <td className="text-sm"><div className="flex items-center gap-2"><TeamLogo team={teamMap[g.away_team_id]} size={24} /><span className="truncate">{teamName(g, "away")}</span></div></td>
                        <td className="font-mono">{g.away_score} — {g.home_score}</td>
                        <td><span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${g.status === "live" ? "bg-[#7FD93C] text-black" : g.status === "final" ? "bg-white/15 text-white" : "bg-[#4FE0D5]/20 text-[#4FE0D5]"}`}>{g.status}</span></td>
                        <td><div className="flex gap-1 justify-end items-center">
                            <Link to={`/live/${g.game_num}`} target="_blank" rel="noreferrer" className="btn-ghost text-[#7FD93C] border-[#7FD93C]/40 hover:border-[#7FD93C] flex items-center gap-1 text-xs" data-testid={`admin-live-${g.game_num}`} title="Ouvrir LiveGame"><Radio size={12} />LIVE</Link>
                            <button onClick={() => startEdit(g)} className="btn-ghost" data-testid={`btn-edit-${g.game_num}`}><Edit2 size={12} /></button>
                            <button onClick={() => del(g.game_num)} className="btn-ghost text-rose-400 border-rose-400/30" data-testid={`btn-del-${g.game_num}`}><Trash2 size={12} /></button>
                        </div></td>
                    </tr>
                ))}{filtered.length === 0 && <tr><td colSpan="11" className="text-center text-white/50 py-12">Aucune partie. Importez votre Excel ou créez-en une.</td></tr>}</tbody>
            </table></div>

            {/* Mobile card view */}
            <div className="lg:hidden grid gap-3">
                {filtered.map(g => (
                    <div key={g.game_num} className="glass-card p-4" data-testid={`game-card-${g.game_num}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                            <div>
                                <span className="font-display text-2xl text-[#7FD93C]">#{g.game_num}</span>
                                <span className="ml-2 px-2 py-0.5 text-[10px] tracking-widest font-bold bg-white/5">{g.category}</span>
                                <span className="ml-1 px-2 py-0.5 text-[10px] font-bold uppercase bg-white/5 text-white/60">{PHASE_LABEL[g.phase] || g.phase}</span>
                            </div>
                            <span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${g.status === "live" ? "bg-[#7FD93C] text-black" : g.status === "final" ? "bg-white/15 text-white" : "bg-[#4FE0D5]/20 text-[#4FE0D5]"}`}>{g.status}</span>
                        </div>
                        <div className="text-xs text-white/60 mb-3">
                            {g.day} · {g.time}{g.time_end ? ` — ${g.time_end}` : ""} · <span className="text-[#4FE0D5]">{g.ice}</span>
                        </div>
                        <div className="space-y-2 mb-3">
                            <div className="flex items-center gap-2">
                                <span className="label-mini w-12 shrink-0">Loc.</span>
                                <TeamLogo team={teamMap[g.home_team_id]} size={28} />
                                <span className="flex-1 min-w-0 truncate text-sm font-semibold">{teamName(g, "home")}</span>
                                <span className="font-mono text-lg shrink-0">{g.home_score}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="label-mini w-12 shrink-0">Vis.</span>
                                <TeamLogo team={teamMap[g.away_team_id]} size={28} />
                                <span className="flex-1 min-w-0 truncate text-sm font-semibold">{teamName(g, "away")}</span>
                                <span className="font-mono text-lg shrink-0">{g.away_score}</span>
                            </div>
                        </div>
                        <div className="flex gap-2 justify-end items-center flex-wrap">
                            <Link to={`/live/${g.game_num}`} target="_blank" rel="noreferrer" className="btn-ghost text-[#7FD93C] border-[#7FD93C]/40 hover:border-[#7FD93C] flex items-center gap-1 text-xs" data-testid={`admin-live-mobile-${g.game_num}`}><Radio size={12} />LIVE</Link>
                            <button onClick={() => startEdit(g)} className="btn-ghost flex items-center gap-1 text-xs"><Edit2 size={12} /> Modifier</button>
                            <button onClick={() => del(g.game_num)} className="btn-ghost text-rose-400 border-rose-400/30 flex items-center gap-1 text-xs"><Trash2 size={12} /> Supprimer</button>
                        </div>
                    </div>
                ))}
                {filtered.length === 0 && <div className="text-center text-white/50 py-12">Aucune partie. Importez votre Excel ou créez-en une.</div>}
            </div>

            {preview && (
                <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto" data-testid="import-preview-modal">
                    <div className="bg-[#0a0a0a] border border-white/10 max-w-3xl w-full my-4 shadow-2xl">
                        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-white/10 sticky top-0 bg-[#0a0a0a] z-10">
                            <div className="flex items-center gap-2 min-w-0">
                                <FileSpreadsheet size={20} className="text-[#4FE0D5] shrink-0" />
                                <h2 className="font-display text-lg sm:text-xl uppercase tracking-widest truncate">Aperçu de l&apos;import</h2>
                            </div>
                            <button onClick={cancelPreview} disabled={importing} className="text-white/50 hover:text-white shrink-0" data-testid="btn-cancel-preview"><X size={20} /></button>
                        </div>

                        <div className="p-4 sm:p-5 space-y-4">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="preview-stats">
                                <div className="bg-black/40 p-3 border-l-2 border-[#4FE0D5]">
                                    <div className="font-display text-2xl">{preview.stats.parsed_rows}</div>
                                    <div className="text-[10px] uppercase tracking-widest text-white/50">Parties</div>
                                </div>
                                <div className="bg-black/40 p-3 border-l-2 border-white/30">
                                    <div className="font-display text-2xl">{preview.stats.unique_teams}</div>
                                    <div className="text-[10px] uppercase tracking-widest text-white/50">Équipes</div>
                                </div>
                                <div className="bg-black/40 p-3 border-l-2 border-[#7FD93C]">
                                    <div className="font-display text-2xl text-[#7FD93C]">{preview.stats.matched_existing}</div>
                                    <div className="text-[10px] uppercase tracking-widest text-white/50">Reliées</div>
                                </div>
                                <div className="bg-black/40 p-3 border-l-2 border-amber-400">
                                    <div className="font-display text-2xl text-amber-400">{preview.stats.would_create_new}</div>
                                    <div className="text-[10px] uppercase tracking-widest text-white/50">Nouvelles</div>
                                </div>
                            </div>

                            {preview.synced_from_registrations > 0 && (
                                <div className="bg-[#4FE0D5]/10 border-l-4 border-[#4FE0D5] p-3 text-xs text-[#4FE0D5]" data-testid="synced-banner">
                                    ✓ {preview.synced_from_registrations} équipe(s) inscrite(s) en attente de paiement ont été ajoutées à la liste des équipes pour permettre le matching.
                                </div>
                            )}

                            {preview.skipped_parse?.length > 0 && (
                                <div className="bg-rose-500/10 border-l-4 border-rose-500 p-3 text-xs text-rose-200">
                                    ⚠️ {preview.skipped_parse.length} ligne(s) ignorée(s) lors du parsing.
                                </div>
                            )}

                            <div>
                                <div className="label-mini mb-2">Association des équipes</div>
                                <p className="text-xs text-white/50 mb-3">Pour chaque équipe détectée dans l&apos;Excel, choisis : relier à une équipe inscrite existante OU créer une nouvelle équipe.</p>
                                <div className="grid gap-2 max-h-[40vh] overflow-y-auto pr-1">
                                    {preview.unique_teams.map(u => {
                                        const current = overrides[u.key] || "__create__";
                                        const isMatched = current && current !== "__create__";
                                        return (
                                            <div key={u.key} className="bg-black/30 p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-testid={`preview-team-${u.key}`}>
                                                <div className="flex items-center gap-2 sm:flex-1 min-w-0">
                                                    {isMatched ? <CheckCircle2 size={16} className="text-[#7FD93C] shrink-0" /> : <AlertCircle size={16} className="text-amber-400 shrink-0" />}
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm font-semibold truncate">{u.excel_name}</div>
                                                        <div className="text-[10px] text-white/40 uppercase tracking-widest">{u.category}</div>
                                                    </div>
                                                </div>
                                                <select
                                                    className="input-field text-sm w-full sm:w-auto sm:min-w-[220px]"
                                                    value={current}
                                                    onChange={e => setOverrides({ ...overrides, [u.key]: e.target.value })}
                                                    data-testid={`override-${u.key}`}
                                                >
                                                    <option value="__create__">➕ Créer « {u.excel_name} »</option>
                                                    {u.category_teams && u.category_teams.length > 0 && (
                                                        <optgroup label={`Équipes ${u.category}`}>
                                                            {u.category_teams.map(t => (
                                                                <option key={t.id} value={t.id}>{t.auto_created ? "⚙ " : ""}{t.name}</option>
                                                            ))}
                                                        </optgroup>
                                                    )}
                                                    {u.other_teams && u.other_teams.length > 0 && (
                                                        <optgroup label="Autres catégories">
                                                            {u.other_teams.map(t => (
                                                                <option key={t.id} value={t.id}>{t.auto_created ? "⚙ " : ""}[{t.category}] {t.name}</option>
                                                            ))}
                                                        </optgroup>
                                                    )}
                                                </select>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <label className="flex items-center gap-3 bg-black/30 p-3 border-l-2 border-amber-400/50 cursor-pointer" data-testid="toggle-replace-all">
                                <input
                                    type="checkbox"
                                    className="w-5 h-5 accent-amber-400 cursor-pointer"
                                    checked={!!preview.replace_all}
                                    onChange={e => setPreview({ ...preview, replace_all: e.target.checked })}
                                />
                                <div className="text-xs">
                                    <div className="font-semibold text-amber-300">Remplacer toutes les parties existantes</div>
                                    <div className="text-white/50">{preview.replace_all ? "⚠️ Toutes les parties actuelles seront SUPPRIMÉES avant l'import." : "Les parties existantes avec le même numéro seront mises à jour, les autres conservées."}</div>
                                </div>
                            </label>
                        </div>

                        <div className="p-4 sm:p-5 border-t border-white/10 flex flex-wrap gap-2 justify-end sticky bottom-0 bg-[#0a0a0a]">
                            <button onClick={cancelPreview} disabled={importing} className="btn-ghost" data-testid="btn-cancel-import">Annuler</button>
                            <button onClick={confirmImport} disabled={importing} className="btn-primary flex items-center gap-2" data-testid="btn-confirm-import">
                                <Save size={14} /> {importing ? "Import en cours..." : "Confirmer l'import"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
